import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import RelatedBlogs from 'components/RelatedBlogs';
import { cloudDevOpsFAQs } from '../data/faqs';
import { useNavigate } from 'react-router-dom';

const CloudDevOps = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Cloud & DevOps Services - AWS, Azure, GCP | OctalChip',
    description: 'Scalable cloud infrastructure setup, migration, and management. We handle AWS, Azure, GCP with automated CI/CD pipelines and monitoring solutions.',
    keywords: 'cloud services, DevOps, AWS, Azure, GCP, CI/CD, Docker, Kubernetes, infrastructure automation',
    canonical: 'https://www.octalchip.com/services/cloud-devops',
    faqData: cloudDevOpsFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Cloud & DevOps Services",
      "description": "Scalable cloud infrastructure setup, migration, and management",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const features = [
    {
      title: 'Cloud Migration',
      description: 'Seamless migration to cloud platforms with zero downtime and data integrity',
      icon: 'ArrowRight'
    },
    {
      title: 'CI/CD Pipelines',
      description: 'Automated build, test, and deployment pipelines for faster releases',
      icon: 'RefreshCw'
    },
    {
      title: 'Infrastructure as Code',
      description: 'Manage infrastructure with code using Terraform, CloudFormation, and Ansible',
      icon: 'Code'
    },
    {
      title: 'Container Orchestration',
      description: 'Deploy and manage containers at scale with Kubernetes and Docker',
      icon: 'Box'
    },
    {
      title: 'Monitoring & Logging',
      description: 'Comprehensive monitoring, alerting, and logging for optimal performance',
      icon: 'Activity'
    },
    {
      title: 'Security & Compliance',
      description: 'Implement security best practices and ensure compliance with industry standards',
      icon: 'Shield'
    }
  ];

  const technologies = [
    { name: 'AWS', category: 'Cloud', description: 'Amazon Web Services - comprehensive cloud platform' },
    { name: 'Azure', category: 'Cloud', description: 'Microsoft Azure cloud services and solutions' },
    { name: 'Google Cloud', category: 'Cloud', description: 'Google Cloud Platform for scalable infrastructure' },
    { name: 'Docker', category: 'Container', description: 'Containerization platform for application deployment' },
    { name: 'Kubernetes', category: 'Orchestration', description: 'Container orchestration for automated deployment' },
    { name: 'Terraform', category: 'IaC', description: 'Infrastructure as Code tool for cloud provisioning' },
    { name: 'Jenkins', category: 'CI/CD', description: 'Automated CI/CD pipeline automation' },
    { name: 'GitLab CI', category: 'CI/CD', description: 'Integrated CI/CD in GitLab platform' },
    { name: 'Ansible', category: 'Automation', description: 'Configuration management and automation tool' }
  ];

  const useCases = [
    {
      title: 'Cloud Migration',
      description: 'Migrate legacy systems to cloud with minimal disruption and maximum efficiency',
      icon: 'Cloud'
    },
    {
      title: 'Microservices Architecture',
      description: 'Design and deploy scalable microservices with container orchestration',
      icon: 'Layers'
    },
    {
      title: 'Auto-scaling Solutions',
      description: 'Implement auto-scaling to handle traffic spikes and optimize costs',
      icon: 'TrendingUp'
    },
    {
      title: 'Disaster Recovery',
      description: 'Build resilient systems with backup, replication, and disaster recovery plans',
      icon: 'Shield'
    },
    {
      title: 'Multi-cloud Strategy',
      description: 'Deploy across multiple cloud providers for redundancy and flexibility',
      icon: 'Globe'
    },
    {
      title: 'DevSecOps',
      description: 'Integrate security into DevOps pipeline for secure and compliant deployments',
      icon: 'Lock'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Assessment & Planning',
      description: 'Evaluate current infrastructure, identify migration needs, and create cloud strategy',
      icon: 'Search'
    },
    {
      step: '02',
      title: 'Architecture Design',
      description: 'Design scalable, secure, and cost-effective cloud architecture',
      icon: 'Layers'
    },
    {
      step: '03',
      title: 'Infrastructure Setup',
      description: 'Provision cloud resources using Infrastructure as Code best practices',
      icon: 'Server'
    },
    {
      step: '04',
      title: 'CI/CD Implementation',
      description: 'Set up automated pipelines for continuous integration and deployment',
      icon: 'RefreshCw'
    },
    {
      step: '05',
      title: 'Migration & Deployment',
      description: 'Execute migration with zero downtime and comprehensive testing',
      icon: 'Rocket'
    },
    {
      step: '06',
      title: 'Monitoring & Optimization',
      description: 'Monitor performance, optimize costs, and ensure continuous improvement',
      icon: 'BarChart'
    }
  ];

  const benefits = [
    'Reduce infrastructure costs by up to 40%',
    'Improve deployment speed and frequency',
    'Increase system reliability and uptime',
    'Scale resources automatically based on demand',
    'Enhance security with cloud-native tools',
    'Enable faster time-to-market for features',
    'Simplify infrastructure management',
    'Improve collaboration between teams'
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Enhanced Hero Section */}
      <section id="hero" className="relative py-20 md:py-32 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00d690 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 md:px-8 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-xl sm:rounded-2xl mb-6 sm:mb-8 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <Icon name="Cloud" size={40} className="sm:w-12 sm:h-12 md:w-14 md:h-14 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-800 mb-4 sm:mb-6 leading-tight px-4">
              Cloud & DevOps
              <span className="block text-[#00d690] mt-1 sm:mt-2">Services</span>
            </h1>
            <div className="w-20 sm:w-24 md:w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-6 sm:mb-8"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
              Transform your infrastructure with scalable cloud solutions and automated DevOps practices. 
              We help you build, deploy, and manage applications with speed, reliability, and efficiency.
            </p>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 px-4">
              <Button 
                variant="primary"
                animation="scale"
                onClick={() => navigate('/services#share-your-requirements')}
                className="px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                Start Your Project
              </Button>
              <Button 
                variant="outline"
                animation="slide"
                onClick={() => navigate('/services#services')}
                className="px-6 sm:px-8 md:px-10 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg border-2"
              >
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-6 sm:py-8 md:py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">20+</div>
              <div className="text-gray-600 font-medium">Cloud Projects</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">2-6</div>
              <div className="text-gray-600 font-medium">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">99.9%</div>
              <div className="text-gray-600 font-medium">Uptime SLA</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">40%</div>
              <div className="text-gray-600 font-medium">Cost Reduction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">DevOps Capabilities</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive DevOps and cloud services for modern applications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:from-[#00d690] group-hover:to-[#00b876] transition-all duration-300">
                  <Icon name={feature.icon} size={20} className="sm:w-6 sm:h-6 md:w-7 md:h-7 text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-[#00d690] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section id="technologies" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Cloud & DevOps Tools</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Industry-leading tools and platforms we work with
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-7xl mx-auto">
            {technologies.map((tech, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">
                    {tech.name}
                  </span>
                  <span className="text-xs font-medium text-[#00d690] bg-[#00d690]/10 px-2 py-1 rounded">
                    {tech.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tech.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#00d690]/5 to-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Cloud Solutions</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Comprehensive cloud and DevOps solutions for every need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group border-l-4 border-[#00d690]"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-[#00d690] transition-all duration-300">
                  <Icon name={useCase.icon} size={20} className="sm:w-6 sm:h-6 md:w-7 md:h-7 text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3 group-hover:text-[#00d690] transition-colors">
                  {useCase.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Our DevOps Process</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              A systematic approach to cloud and DevOps implementation
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d690] to-[#00b876]"></div>
              
              <div className="space-y-8 sm:space-y-10 md:space-y-12">
                {processSteps.map((step, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-start gap-4 sm:gap-6">
                    <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base md:text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    
                    <div className="flex-1 bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                      <div className="flex items-center mb-3 sm:mb-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                          <Icon name={step.icon} size={18} className="sm:w-5 sm:h-5 md:w-6 md:h-6 text-[#00d690]" />
                        </div>
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8 sm:mb-12 md:mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Benefits of Cloud & DevOps</h2>
              <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 sm:gap-4 bg-white/5 backdrop-blur-sm p-4 sm:p-6 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-[#00d690] rounded-full flex items-center justify-center">
                    <Icon name="Check" size={14} className="sm:w-5 sm:h-5 text-white" />
                  </div>
                  <p className="text-sm sm:text-base md:text-lg text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-6 sm:mb-8 md:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
              Ready to Modernize Your Infrastructure?
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 px-4">
              Let's discuss your cloud and DevOps needs and create a solution that scales with your business. 
              Get a free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Button
                variant="primary"
                animation="pulse"
                onClick={() => navigate('/services#share-your-requirements')}
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-3 sm:py-4 px-6 sm:px-8 transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
              >
                Start Your Project
              </Button>
              <Button
                variant="outline"
                animation="bounce"
                onClick={() => navigate('/services#services')}
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-3 sm:py-4 px-6 sm:px-8 transition-all duration-300 text-sm sm:text-base"
              >
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={cloudDevOpsFAQs}
        title="Cloud & DevOps FAQs"
        description="Common questions about cloud migration, DevOps, CI/CD, and infrastructure management."
      />

      {/* Related Blogs Section */}
      <RelatedBlogs
        servicePath="/services/cloud-devops"
        serviceTags={['Cloud DevOps', 'AWS', 'Azure', 'GCP', 'Docker', 'Kubernetes', 'CI/CD', 'DevOps']}
        serviceName="Cloud DevOps"
        limit={6}
        title="Cloud & DevOps Related Blogs"
      />

      <Footer />
    </div>
  );
};

export default CloudDevOps;
